"use client"

import { useEffect, useState, useCallback } from "react"
import { Table, Card, Button, Input, Space, Modal, Upload, Form, InputNumber, message, Image } from "antd"
import {
  SearchOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import {
  getLandingPageImages,
  createLandingPageImage,
  updateLandingPageImage,
  deleteLandingPageImage,
  reorderLandingPageImages,
} from "../../services/LandingPageServices"
import { useAuth } from "../../contexts/authContext/index"

const { confirm } = Modal
const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL

const LandingPageImages = () => {
  const [imagesData, setImagesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDetailVisible, setImageDetailVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { getToken } = useAuth();

  // Memoize fetchImages function to prevent unnecessary re-renders
  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const images = await getLandingPageImages(token);
      setImagesData(images);
    } catch (error) {
      console.error("Error fetching landing page images:", error);
      message.error("Error al cargar las imágenes");
    } finally {
      setLoading(false);
    }
  }, [getToken]); // Add getToken as a dependency

  // Fetch all images on component mount
  useEffect(() => {
    fetchImages();
  }, [fetchImages]); 

  // Filter data based on search text
  const filteredData = imagesData.filter((image) => {
    if (searchText === "") return true;
    const searchValue = searchText.toLowerCase();
    
    // Search in position (convert to string first)
    return String(image.position).includes(searchValue);
  });

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleViewDetails = (image) => {
    setSelectedImage(image);
    setImageDetailVisible(true);
  };

  const handleAddImage = () => {
    form.resetFields();
    setFileList([]);
    setAddModalVisible(true);
  };

  const handleEditImage = (image) => {
    setSelectedImage(image);
    editForm.setFieldsValue({
      position: image.position,
    });
    setFileList([]);
    setEditModalVisible(true);
  };

  const handleDeleteImage = async (imageId) => {
    confirm({
      title: '¿Estás seguro de eliminar esta imagen?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      async onOk() {
        try {
          const token = await getToken();
          await deleteLandingPageImage(token, imageId);
          message.success('Imagen eliminada correctamente');
          fetchImages(); // Refresh the list
        } catch (error) {
          console.error("Error deleting image:", error);
          message.error('Error al eliminar la imagen');
        }
      },
    });
  };

  const handleReorderImages = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      await reorderLandingPageImages(token);
      message.success('Imágenes reordenadas correctamente');
      fetchImages(); // Refresh the list with new order
    } catch (error) {
      console.error("Error reordering images:", error);
      message.error('Error al reordenar las imágenes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAdd = async (values) => {
    if (fileList.length === 0) {
      message.error("Por favor seleccione una imagen");
      return;
    }

    try {
      setUploadLoading(true);
      const token = await getToken();
      const file = fileList[0].originFileObj || fileList[0];
      
      await createLandingPageImage(token, file, values.position || 0);
      
      message.success("Imagen agregada exitosamente");
      setAddModalVisible(false);
      form.resetFields();
      setFileList([]);
      fetchImages();
    } catch (error) {
      console.error("Error adding image:", error);
      message.error("Error al agregar la imagen");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleSubmitEdit = async (values) => {
    try {
      setUploadLoading(true);
      const token = await getToken();
      
      let file = null;
      if (fileList.length > 0) {
        file = fileList[0].originFileObj || fileList[0];
      }

      await updateLandingPageImage(token, selectedImage.id, file, values.position);

      message.success("Imagen actualizada exitosamente");
      setEditModalVisible(false);
      editForm.resetFields();
      setFileList([]);
      setSelectedImage(null);
      fetchImages();
    } catch (error) {
      console.error("Error updating image:", error);
      message.error("Error al actualizar la imagen");
    } finally {
      setUploadLoading(false);
    }
  };

  // Custom upload button for Ant Design Upload component
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Subir</div>
    </div>
  );

  // Table columns configuration
  const columns = [
    {
      title: "Imagen",
      dataIndex: "image_path",
      key: "image",
      render: (image_path) => {
        // Generate download URL for display
        const imageUrl = image_path ? `${REACT_APP_BFF_URL}/api/file-upload/download?path=${encodeURIComponent(image_path)}` : null;
        return imageUrl ? (
        <Image 
            src={imageUrl} 
          alt="Landing page image" 
          style={{ width: 100, height: 60, objectFit: 'cover' }} 
          />
        ) : (
          <div style={{ width: 100, height: 60, backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Sin imagen
          </div>
        );
      },
    },
    {
      title: "Posición",
      dataIndex: "position",
      key: "position",
      sorter: (a, b) => a.position - b.position,
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEditImage(record)}
          >
            Editar
          </Button>
          <Button 
            type="danger" 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteImage(record.id)}
          >
            Eliminar
          </Button>
          <Button 
            type="link" 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            Ver
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Gestión de Imágenes de Página de Inicio"
        extra={
          <Space>
            <Input 
              placeholder="Buscar por posición" 
              prefix={<SearchOutlined />} 
              style={{ width: 200 }} 
              value={searchText}
              onChange={handleSearch}
              allowClear
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddImage}>
              Agregar Imagen
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReorderImages}>
              Reordenar
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowKey="id"
        />
      </Card>

      {/* Modal para ver detalles de la imagen */}
      <Modal
        title="Detalles de la Imagen"
        open={imageDetailVisible}
        onCancel={() => setImageDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setImageDetailVisible(false)}>
            Cerrar
          </Button>
        ]}
        width={700}
      >
        {selectedImage && (
          <div style={{ textAlign: 'center' }}>
            {selectedImage.image_path ? (
            <Image 
                src={`${REACT_APP_BFF_URL}/api/file-upload/download?path=${encodeURIComponent(selectedImage.image_path)}`}
              alt="Landing page image" 
              style={{ maxWidth: '100%', maxHeight: '400px' }} 
              />
            ) : (
              <div style={{ 
                width: '100%', 
                height: '400px', 
                backgroundColor: '#f0f0f0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                Sin imagen
              </div>
            )}
            <p style={{ marginTop: 16 }}>
              <strong>Posición:</strong> {selectedImage.position}
            </p>
            {selectedImage.filename && (
              <p>
                <strong>Archivo:</strong> {selectedImage.filename}
              </p>
            )}
          </div>
        )}
      </Modal>

      {/* Modal para agregar una nueva imagen */}
      <Modal
        title="Agregar Nueva Imagen"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        confirmLoading={uploadLoading}
        footer={[
          <Button key="back" onClick={() => setAddModalVisible(false)}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" loading={uploadLoading} onClick={() => form.submit()}>
            Guardar
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitAdd}
        >
          <Form.Item
            name="image"
            label="Imagen"
            rules={[{ required: true, message: 'Por favor selecciona una imagen' }]}
          >
            <Upload
              listType="picture-card"
              beforeUpload={() => false}
              maxCount={1}
              accept="image/*"
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
            >
              {uploadButton}
            </Upload>
          </Form.Item>
          <Form.Item
            name="position"
            label="Posición"
            rules={[{ required: true, message: 'Por favor ingresa la posición' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para editar una imagen existente */}
      <Modal
        title="Editar Imagen"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={uploadLoading}
        footer={[
          <Button key="back" onClick={() => setEditModalVisible(false)}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" loading={uploadLoading} onClick={() => editForm.submit()}>
            Actualizar
          </Button>,
        ]}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleSubmitEdit}
        >
          {selectedImage && (
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <p>Imagen actual:</p>
              {selectedImage.image_path ? (
              <Image 
                  src={`${REACT_APP_BFF_URL}/api/file-upload/download?path=${encodeURIComponent(selectedImage.image_path)}`}
                alt="Current image" 
                style={{ maxWidth: '100%', maxHeight: '200px' }} 
                />
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: '200px', 
                  backgroundColor: '#f0f0f0', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  Sin imagen
                </div>
              )}
            </div>
          )}
          <Form.Item
            name="image"
            label="Nueva Imagen (opcional)"
          >
            <Upload
              listType="picture-card"
              beforeUpload={() => false}
              maxCount={1}
              accept="image/*"
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
            >
              {uploadButton}
            </Upload>
          </Form.Item>
          <Form.Item
            name="position"
            label="Posición"
            rules={[{ required: true, message: 'Por favor ingresa la posición' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LandingPageImages;